import cx from 'classnames';
import {addDays, format, parseISO, startOfWeek} from 'date-fns';
import NextLink from 'next/link';

import {Button, Text} from '#component/base';
import {sprinkles} from '#style/sprinkles';

import {FooterCTAProps} from './FooterCTA.types';

/**
 * This component renders the "Call to Action" portion of the Check In page's
 * result screen's footer. The content is conditionally rendered based on if the
 * user has an active subscription, and/or a selected actionPlan.
 */
export const FooterCTA = ({
  className,
  nextCheckInDate,
  hasActionPlan,
  hasActiveSubscription,
  ...otherProps
}: FooterCTAProps) => {
  if (!hasActiveSubscription) {
    return (
      <div
        className={cx(sprinkles({dir: 'col', flex: 'flex', gap: 32}), className)}
        {...otherProps}>
        <p className={sprinkles({dir: 'col', flex: 'flex', items: 'center'})}>
          <Text color="deepBurgundy" fontWeight="semiBold" textAlign="center">
            Your membership is inactive
          </Text>

          <Text color="deepBurgundy" textAlign="center">
            Subscribe to access additional features such as Action Plans
          </Text>
        </p>

        <NextLink href="/profile?tab=manage" passHref>
          <Button as="a">Subscribe</Button>
        </NextLink>
      </div>
    );
  }

  if (!hasActionPlan) {
    return (
      <div
        className={cx(sprinkles({dir: 'col', flex: 'flex', gap: 32}), className)}
        {...otherProps}>
        <p className={sprinkles({dir: 'col', flex: 'flex', items: 'center'})}>
          <Text color="deepBurgundy" fontWeight="semiBold" textAlign="center">
            Your Action Plan is inactive
          </Text>

          <Text color="deepBurgundy" textAlign="center">
            Select a plan to continue accessing Tally Health™ Check-ins
          </Text>
        </p>

        <NextLink href="/actionplan" passHref>
          <Button as="a">View Action Plans</Button>
        </NextLink>
      </div>
    );
  }

  const renderedDate = nextCheckInDate
    ? parseISO(nextCheckInDate)
    : startOfWeek(addDays(new Date(), 7), {weekStartsOn: 1});

  return (
    <div className={cx(sprinkles({dir: 'col', flex: 'flex', gap: 32}), className)} {...otherProps}>
      <p className={sprinkles({dir: 'col', flex: 'flex', items: 'center'})}>
        <Text color="deepBurgundy" textAlign="center">
          Your next check-in is
        </Text>
        <Text color="deepBurgundy" variant="bodyXLarge" textAlign="center">
          {format(renderedDate, 'EEEE, MMMM d')}
        </Text>
      </p>

      <NextLink href="/dashboard" passHref>
        <Button as="a">Got it</Button>
      </NextLink>
    </div>
  );
};
